<i18n>
{
  "ja": {
    "noResults": "倉庫データがありません。",
    "sorry": "申し訳ありません！",
    "noWarehouseFound": "ご希望の条件では、倉庫が見つかりませんでした。",
    "changeCriteriaAndSearchAgain": "条件を変更して検索してみましょう！",
    "inquiryListReset": "荷物タイプを変更すると、引き合いリストの内容がリセットされます。",
    "change": "変更する"
  },
  "en": {
    "noResults": "No Results",
    "sorry": "Sorry!",
    "noWarehouseFound": "We could not find any warehouses with the specified criteria.",
    "changeCriteriaAndSearchAgain": "Please change the criteria and search again",
    "change": "Proceed"
  }
}
</i18n>

<template>
  <section class="flex-grow flex flex-col relative">
    <div id="map-area" ref="mapArea" :style="mapAreaStyle">
      <div v-if="hasNoResult" class="no-result-overlay">
        <div class="no-result h-full p-10 flex flex-col md:flex-row items-center justify-center content-center">
          <img src="@/assets/img/disappointed_shipper.svg" alt="No Search Results">
          <div class="text-white mt-4 md:mt-0 md:ml-8 text-center md:text-left">
            <h1 class="font-bold text-white text-xl mb-6">
              {{ $t('sorry') }}
              <br>
              {{ $t('noWarehouseFound') }}
            </h1>
            <p class="text-sm">
              {{ $t('changeCriteriaAndSearchAgain') }}
            </p>
          </div>
        </div>
      </div>
      <client-only>
        <gmap-map
          ref="gmap"
          :center="mapCenter"
          :zoom="zoom"
          :options="mapOptions"
          style="height: 100%;"
          class="border-none w-1/1"
        >
          <gmap-circle
            :center="mapCenter"
            :radius="searchRadius"
            :options="{
              strokeColor: '#79C7BC',
              strokeOpacity: 0.8,
              strokeWeight: 0,
              fillColor: '#79C7BC',
              fillOpacity: 0.35
            }"
          />
          <gmap-marker
            ref="markerCenter"
            :position="mapCenter"
            :clickable="false"
            :draggable="false"
            :icon="{url: require('~/assets/img/marker_center.svg')}"
          />
          <gmap-marker
            v-for="(w, index) in warehouses"
            :key="w.name"
            :position="{ lat: w.lat, lng: w.lng }"
            :clickable="true"
            :draggable="false"
            :icon="markerIcon(w)"
            @click="onSelect(w, index)"
          />
        </gmap-map>
      </client-only>
    </div>
  </section>
</template>

<style lang="sass" scoped>
#map-area
  position: relative
  .no-result-overlay
    z-index: 1
    position: absolute
    top: 0
    left: 0
    right: 0
    bottom: 0
    background-color: rgba(0, 0, 0, 0.6)

#map-area ::v-deep .vue-map-container .vue-map
  @apply border-gray-300
  @media (max-width: theme('screens.md'))
    @apply border-0
</style>

<script>
/* global google */
import { gmapApi } from 'vue2-google-maps';
import { mapGetters } from 'vuex';
import { isEmpty } from '@/components/utils/string';
import PageHeader from '@/components/pageHeader';
import styles from './mapStyles';

const MIN_ZOOM = 8;
const MAX_ZOOM = 12;
const DEFAULT_ZOOM = 9;
const SELECTED_ZOOM = MAX_ZOOM;

export default {
  name: 'SearchMap',
  components: {
    PageHeader
  },
  props: {
    isLocationReady: { type: Boolean, required: true },
    isLoading: { type: Boolean, required: true },
    mapCenter: { type: Object, required: true },
    searchDistance: { type: Number, required: true },
    selectedWarehouse: { type: Object, default: null },
    warehouses: { type: Array, required: true },
    searchLocationHeight: { type: Number, default: 95 }
  },
  data () {
    return {
      gmap: null,
      geocoder: null,
      googleMapsInitialized: false,
      zoom: DEFAULT_ZOOM
    };
  },

  computed: {
    google: gmapApi,
    ...mapGetters('nav', ['isMobile', 'isDesktop', 'headerHeight']),

    mapAreaStyle () {
      if (this.isMobile) return { height: `calc(100vh - ${this.headerHeight}px - ${this.searchLocationHeight}px)` };
      return { height: `calc(100vh - ${this.headerHeight}px)` };
    },
    mapOptions() {
      return {
        disableDefaultUI: true,
        zoomControl: !this.isMobile,
        styles,
        minZoom: MIN_ZOOM,
        maxZoom: MAX_ZOOM
      };
    },

    showResult () {
      return this.isLocationReady && !this.isLoading;
    },

    hasNoResult () {
      return this.showResult && this.warehouses.length === 0;
    },

    searchRadius () {
      return this.searchDistance * 1000;
    }
  },

  watch: {
    selectedWarehouse (next, prev) {
      if (next.id !== prev.id && next.index >= 0) {
        this.selectWarehouse(next, next.index);
      }
      else if (prev.index >=0 && next.id === null) {
        this.unselectWarehouse();
      }
    }
  },

  async mounted () {
    await this.$nextTick();
    await this.$refs.gmap.$mapPromise;
    const map = await this.$refs.gmap.$mapObject;
    this.gmap = map;
    this.geocoder = new google.maps.Geocoder();
    this.googleMapsInitialized = true;
    this.$emit('initialized');
  },

  methods: {
    markerIcon (w) {
      return {
        url: w.id === this.selectedWarehouse.id
          ? require('~/assets/img/marker_red.svg')
          : require('~/assets/img/marker_green.svg')
      };
    },

    invokeGeocodeAddress (value) {
      if (isEmpty(value)) return;
      this.geocoder.geocode({ 'address': value }, (results, status) => {
        if (status === 'OK') {
          this.$emit('location-found',
            {
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng()
            }
          );
        } else {
          console.warn('Geocode was not successful for the following reason: ' + status, results);
          this.$emit('location-found', null);
        }
      });
    },

    onSelect (w, index) {
      this.$emit('select', w, index);
    },

    unselectWarehouse () {
      this.$emit('unselect');
      this.gmap.panTo(new google.maps.LatLng(this.mapCenter.lat, this.mapCenter.lng), DEFAULT_ZOOM);
      google.maps.event.addListenerOnce(this.gmap, 'idle', () => {
        this.gmap.setZoom(DEFAULT_ZOOM);
      });
    },

    selectWarehouse (w, index) {
      this.gmap.panTo(new google.maps.LatLng(w.address.lat, w.address.lng), SELECTED_ZOOM);
      google.maps.event.addListenerOnce(this.gmap, 'idle', () => {
        this.gmap.setZoom(SELECTED_ZOOM);
      });
    }
  }
};
</script>
